// Roles y Permisos · pestaña «Organizar la matriz»: la lógica que no necesita Angular (con prueba en node:test).
// Ojo: solo anota `x: any | string | number | boolean` y trae `interface` al borde izquierdo, sin `as` ni genéricos,
// porque la prueba le quita los tipos a mano (CI no instala `typescript`).

export const AVISO_ORGANIZAR = 'Cambiar nombres y orden no cambia quién tiene permiso.';
export const AL_FINAL = '__al_final__';

export interface PlanOrden {
  mover: any;
  despuesDe?: any;
  alInicio?: any;
}

/** Lo que va en `cambios` (fila) o en el cuerpo (módulo) de la API: `alInicio: true` o `despuesDe`, nunca los dos. */
export const cambiosDePlan = (plan: any) => (plan.alInicio ? { alInicio: true } : { despuesDe: plan.despuesDe });

/** ¿Lo tocó alguien desde la interfaz? (`interfaz` o `mixto`). */
export const esEditado = (origen: any) => origen === 'interfaz' || origen === 'mixto';

/** Etiqueta de una casilla tocada desde la interfaz. El árbol solo trae `origen`: no distingue creada de renombrada. */
export const etiquetaOrigenCasilla = (origen: any) => (origen === 'mixto' ? '·\u00a0editada' : '·\u00a0desde la interfaz');

const limpio = (t: any) => String(t == null ? '' : t).trim().replace(/\s+/g, ' ');
const clave = (t: any) => limpio(t).toLowerCase();

/**
 * Subir o bajar un elemento de una lista de claves (nombres de fila o ids de módulo).
 * La API sabe «ponlo después de X» (`despuesDe`, y null = al final) y «ponlo de primero» (`alInicio: true`).
 *   - subir i >= 2  -> mover i después de lista[i-2]
 *   - subir i == 1  -> mover i de primero (alInicio)
 *   - bajar i       -> mover i después de lista[i+1]       (null si ese es el último, para dejarlo al final)
 *   - subir el primero o bajar el último -> null (no hay a dónde)
 * @returns { mover, despuesDe } o null
 */
export const planSubirBajar = (claves: any, elegida: any, dir: any) => {
  const lista = claves || [];
  const i = lista.indexOf(elegida);
  if (i < 0) return null;
  if (dir === 'subir') {
    if (i === 0) return null;
    if (i === 1) return { mover: lista[1], alInicio: true };
    return { mover: lista[i], despuesDe: lista[i - 2] };
  }
  if (dir === 'bajar') {
    if (i >= lista.length - 1) return null;
    return { mover: lista[i], despuesDe: i + 1 === lista.length - 1 ? null : lista[i + 1] };
  }
  return null;
};

/** Mover una casilla a la izquierda (`antes`) o a la derecha (`despues`) dentro de su fila. null si ya está en la orilla. */
export const planMoverCasilla = (casillas: any, code: any, dir: any) => {
  const lista = ordenarCasillas(casillas);
  const i = lista.findIndex((c: any) => c.code === code);
  if (i < 0) return null;
  if (dir === 'izquierda') return i === 0 ? null : { code, mover: 'antes' };
  if (dir === 'derecha') return i === lista.length - 1 ? null : { code, mover: 'despues' };
  return null;
};

export const ordenarCasillas = (casillas: any) =>
  (casillas || []).slice().sort((a: any, b: any) => (a.orden || 0) - (b.orden || 0));

/** Nombre vacío, demasiado largo o repetido entre `existentes` (sin contar `actual`, que es el nombre que ya tiene). '' = sin problema. */
export const validarNombre = (nombre: any, existentes: any, actual: any) => {
  const n = limpio(nombre);
  if (!n) return 'Escribe un nombre.';
  if (n.length > 80) return 'El nombre es muy largo (máximo 80 letras).';
  if (actual != null && clave(n) === clave(actual)) return '';
  if ((existentes || []).some((e: any) => clave(e) === clave(n))) return 'Ya existe uno con ese nombre en ese lugar.';
  return '';
};

/** Las filas de un grupo de una pantalla, en el orden en que la matriz las dibuja. `grupo` '' = filas sueltas. */
export const filasDeGrupo = (pantalla: any, grupo: any) =>
  ((pantalla && pantalla.filas) || [])
    .map((f: any, i: any) => ({ f, i }))
    .filter((x: any) => (x.f.grupo || '') === (grupo || ''))
    .sort((a: any, b: any) => (a.f.min - b.f.min) || (a.i - b.i))
    .map((x: any) => x.f);

/** Los grupos que se muestran de una pantalla: las filas sueltas (si hay) y cada grupo, cada uno con sus filas. */
export const seccionesDePantalla = (pantalla: any) => {
  const out = [];
  const sueltas = filasDeGrupo(pantalla, '');
  if (sueltas.length) out.push({ id: '', nombre: 'Sin grupo', origen: 'catalogo', filas: sueltas });
  for (const g of (pantalla && pantalla.grupos) || []) {
    out.push({ id: g.id, nombre: g.nombre, origen: g.origen, filas: filasDeGrupo(pantalla, g.id) });
  }
  return out;
};

export const llaveFila = (f: any) => `${f.module}.${f.submodule}`;

/**
 * «Mover a…»: revisa el destino antes de pedirlo. `destino` = { pantalla, grupo, despuesDe } (despuesDe: nombre de fila o AL_FINAL).
 * Devuelve { error, cambios }: `cambios` listo para PUT /fila (solo lo que cambia de sitio; despuesDe null = al final).
 */
export const planMoverFila = (pantallas: any, fila: any, pantallaActual: any, destino: any) => {
  const pant = (pantallas || []).find((p: any) => p.id === destino.pantalla);
  if (!pant) return { error: 'Elige el módulo al que lo quieres mover.', cambios: null };
  const grupo = destino.grupo || '';
  if (grupo && !(pant.grupos || []).some((g: any) => g.id === grupo)) return { error: 'Elige un grupo de ese módulo.', cambios: null };
  const mismoLugar = pant.id === pantallaActual && grupo === (fila.grupo || '');
  const enDestino = filasDeGrupo(pant, grupo).filter((f: any) => llaveFila(f) !== llaveFila(fila));
  if (!mismoLugar && enDestino.some((f: any) => clave(f.nombre) === clave(fila.nombre))) {
    return { error: `Ya hay una fila llamada «${fila.nombre}» en ese lugar. Cámbiale el nombre primero.`, cambios: null };
  }
  let despuesDe = null;
  if (destino.despuesDe && destino.despuesDe !== AL_FINAL) {
    if (!enDestino.some((f: any) => f.nombre === destino.despuesDe)) return { error: 'La fila que elegiste ya no está en ese lugar.', cambios: null };
    despuesDe = destino.despuesDe;
  }
  const cambios: any = { despuesDe };
  if (!mismoLugar) { cambios.pantalla = pant.id; cambios.grupo = grupo; }
  return { error: '', cambios };
};

// ── Arrastrar, plegar, buscar y eliminar ──

/** El módulo «otros» ya no existe: se oculta cuando no tiene filas (el API también lo excluye). */
export const pantallasVisibles = (pantallas: any) =>
  (pantallas || []).filter((p: any) => !(p.id === 'otros' && !(p.filas || []).length));

/** Del índice de la fila sobre la que pasa el cursor y la mitad (de arriba o de abajo), el hueco donde caería: 0 = antes de la primera, n = al final. */
export const huecoDeSoltar = (indice: any, mitadInferior: any) => indice + (mitadInferior ? 1 : 0);

/**
 * Soltar `origen` en el hueco `hueco` (0..n; hueco k = justo antes del elemento k de la lista; n = al final).
 * Devuelve la lista de pasos `{ mover, despuesDe }` o `{ mover, alInicio: true }` (despuesDe null = al final) que la API
 * debe aplicar, o null si no cambia nada. Siempre es 1 paso (de primero = `alInicio`).
 */
export const planArrastrar = (claves: any, origen: any, hueco: any) => {
  const lista = claves || [];
  const i = lista.indexOf(origen);
  if (i < 0 || typeof hueco !== 'number' || hueco < 0 || hueco > lista.length) return null;
  const destino = hueco > i ? hueco - 1 : hueco;
  if (destino === i) return null;
  if (destino > 0) {
    return [{ mover: origen, despuesDe: destino >= lista.length - 1 ? null : lista.filter((x: any) => x !== origen)[destino - 1] }];
  }
  return [{ mover: origen, alInicio: true }];
};

/** Una sola fila abierta a la vez: tocar la abierta la cierra. */
export const alternarAbierta = (actual: any, clave: any) => (actual === clave ? '' : clave);

/** ¿Un grupo se ve desplegado? Lo que el usuario eligió; si no ha elegido, abierto solo cuando el módulo tiene un único grupo. */
export const grupoAbierto = (estado: any, id: any, totalSecciones: any) =>
  estado && typeof estado[id] === 'boolean' ? estado[id] : totalSecciones === 1;

const sinTildes = (t: any) => limpio(t).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Filas cuyo nombre contiene el texto (sin mayúsculas ni tildes). Texto vacío = todas. */
export const filtrarFilas = (filas: any, texto: any) => {
  const q = sinTildes(texto);
  if (!q) return filas || [];
  return (filas || []).filter((f: any) => sinTildes(f.nombre).includes(q));
};

/** Las secciones con el buscador aplicado: con texto, solo quedan los grupos que tienen coincidencias. */
export const seccionesFiltradas = (secciones: any, texto: any) => {
  if (!sinTildes(texto)) return secciones || [];
  return (secciones || [])
    .map((s: any) => ({ id: s.id, nombre: s.nombre, origen: s.origen, filas: filtrarFilas(s.filas, texto) }))
    .filter((s: any) => s.filas.length > 0);
};

export const AYUDA_ELIMINAR = 'Para eliminarlo, mueve antes sus filas a otro lugar';

/** Eliminar solo se ofrece para lo creado desde la interfaz y sin filas. */
export const puedeEliminarGrupo = (seccion: any) =>
  !!seccion && !!seccion.id && seccion.origen === 'interfaz' && !(seccion.filas || []).length;

export const puedeEliminarPantalla = (pantalla: any) =>
  !!pantalla && pantalla.origen === 'interfaz' && !(pantalla.filas || []).length;

/** Lo creado desde la interfaz pero con filas: no se puede eliminar y se muestra el texto de ayuda. */
export const ayudaEliminar = (origen: any, cantidadFilas: any) =>
  origen === 'interfaz' && cantidadFilas > 0 ? AYUDA_ELIMINAR : '';

/** Lo que el servidor contesta, en lenguaje claro. */
export const errorDeApi = (error: any, quePasaba: any) => {
  const status = error && error.status;
  const msg = error && error.error && error.error.msg;
  if (status === 409) return msg || 'Ya existe uno con ese nombre en ese lugar. Elige otro nombre.';
  if (status === 403) return 'No tienes permiso para organizar la matriz.';
  if (status === 404) return 'Eso ya no está en la matriz. Se actualizó la lista.';
  if (status === 400 && msg) return msg;
  return msg || `No se pudo ${quePasaba}. Inténtalo de nuevo.`;
};
